import React from "react";
import {
  Typography,
  makeStyles,
  createStyles,
  Button,
  useTheme,
  Badge,
  IconButton,
  Grid,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import EditIcon from "@material-ui/icons/Edit";
import ClearIcon from "@material-ui/icons/Clear";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import { CustomTheme } from "../../../../types/customTheme";
import _ from "lodash";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../utils/enum";

export interface BoxDetailsProps {
  widgetData: any;
  setIsOpenTextEditModal: any;
  setEditableWidget: any;
  setIsOpenDeleteConfirmationModal: any;
  layout: any;
  setLayout: any;
  setSelectedWidgets: any;
  id: any;
  selectedWidgets: any;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    root: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
      "&:hover $iconButtons": {
        opacity: 1,
        pointerEvents: "auto",
      },
    },
    rootNormal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "transparent",
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
      "&:hover $iconButtons": {
        opacity: 1,
        pointerEvents: "auto",
      },
    },
    normal: {
      color: theme.palette.custom.orange.contrastText,
      transition: "color 1s ease",
    },

    iconButtons: {
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 0.2s ease",
    },
  }),
);

const TextWidgetBox: React.FunctionComponent<BoxDetailsProps> = ({
  widgetData,
  setIsOpenTextEditModal,
  setEditableWidget,
  setIsOpenDeleteConfirmationModal,
  layout,
  setLayout,
  setSelectedWidgets,
  id,
  selectedWidgets,
}) => {
  const classes = useStyles();

  const handleEdit = () => {
    setIsOpenTextEditModal(true);
    setEditableWidget(widgetData?.data);
  };

  const handleDelete = () => {
    setEditableWidget(widgetData?.data);
    setIsOpenDeleteConfirmationModal(true);
  };

  const handleDecrementAverageValue = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.averageTitleFontSize ?? 28;
        data.data["averageTitleFontSize"] = currentSize - 1;
      }
    });
    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  const handleDecrementSubTitleAverageValue = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.averageSubTitleFontSize ?? 12;
        data.data["averageSubTitleFontSize"] = currentSize - 1;
      }
    });
    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  const handleIncrementAverageValue = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.averageTitleFontSize ?? 28;
        data.data["averageTitleFontSize"] = currentSize + 1;
      }
    });

    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  const handleIncrementSubTitleAverageValue = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.averageSubTitleFontSize ?? 12;
        data.data["averageSubTitleFontSize"] = currentSize + 1;
      }
    });

    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  function generateKey(length = 8) {
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";
    for (let i = 0; i < length; i++) {
      key += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return key;
  }
  const handleCopy = (data: any) => {
    const newKey = generateKey();
    const cloneWidget: any = _.cloneDeep(data);

    const cloneSelectedWidgets = _.cloneDeep(selectedWidgets);
    const cloneLayout = _.cloneDeep(layout);

    cloneWidget["key"] = newKey;
    cloneSelectedWidgets.push(cloneWidget);
    setSelectedWidgets(cloneSelectedWidgets);

    // Step 1: Build a map of occupied cells: occupied[x][y] = true
    const occupied: { [x: number]: Set<number> } = {};
    for (let x = 0; x < 12; x++) {
      occupied[x] = new Set<number>();
    }

    cloneLayout.forEach((item: any) => {
      for (let dx = 0; dx < item.w; dx++) {
        for (let dy = 0; dy < item.h; dy++) {
          const px = item.x + dx;
          const py = item.y + dy;
          if (!occupied[px]) occupied[px] = new Set<number>();
          occupied[px].add(py);
        }
      }
    });

    // Step 2: Find first available (x, y) slot
    let targetX = 0;
    let targetY = 0;
    let found = false;

    for (let y = 0; y < 100 && !found; y++) {
      // limit scan depth
      for (let x = 0; x < 12 && !found; x++) {
        if (!occupied[x].has(y)) {
          targetX = x;
          targetY = y;
          found = true;
        }
      }
    }
    cloneWidget["key"] = newKey;
    // Step 3: Add new widget to found (x, y)
    const newLayoutItem = {
      i: newKey,
      x: targetX,
      y: targetY,
      w: 3,
      h: 1,
      data: cloneWidget,
    };
    cloneLayout.push(newLayoutItem);
    setLayout(cloneLayout);
  };

  const theme: CustomTheme = useTheme();
  return (
    <Badge
      badgeContent={
        <IconButton
          className={`"no-drag" ${classes.iconButtons}`}
          //  className="no-drag"
          size="small"
          onClick={handleDelete}
          style={{
            backgroundColor: theme.palette.custom.red.main,
            color: "white",
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      }
      className={
        widgetData?.data?.main === "headBox" ? classes.root : classes.rootNormal
      }
      // overlap="circular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Grid container>
        <Grid item xs={12}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent:
                widgetData?.data?.["align"] === "left"
                  ? "start"
                  : widgetData?.data?.["align"] === "right"
                  ? "end"
                  : "center",
            }}
          >
            {widgetData?.data?.title && (
              <Tooltip
                title={`Value Text Size: ${
                  widgetData?.data?.["averageTitleFontSize"] ?? 28
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    backgroundColor: theme.palette.custom.grey.light,
                    marginRight: "8px",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                  onClick={handleDecrementAverageValue}
                >
                  <RemoveIcon />
                </Button>
              </Tooltip>
            )}
            <Typography
              variant="h4"
              align={
                widgetData?.data?.["align"] ? widgetData["align"] : "center"
              }
              style={{
                fontSize: `${
                  widgetData?.data?.["averageTitleFontSize"] ?? 28
                }px`,
                fontWeight: "bold",
              }}
              className={classes.normal}
            >
              {widgetData?.data?.title}
            </Typography>

            {widgetData?.data?.title && (
              <Tooltip
                title={`Value Text Size: ${
                  widgetData?.data?.["averageTitleFontSize"] ?? 28
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    color: theme.palette.custom.orange.contrastText,
                    marginLeft: "8px",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                  onClick={handleIncrementAverageValue}
                >
                  <AddIcon />
                </Button>
              </Tooltip>
            )}
          </div>
        </Grid>
        <Grid item xs={12} style={{ marginTop: "8px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent:
                widgetData?.data?.["align"] === "left"
                  ? "start"
                  : widgetData?.data?.["align"] === "right"
                  ? "end"
                  : "center",
              width: "100%",
              zIndex: 0,
            }}
          >
            {widgetData?.data?.subTitle && (
              <Tooltip
                title={`Value Text Size: ${
                  widgetData?.data?.["averageSubTitleFontSize"] ?? 12
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    marginRight: "8px",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                  onClick={handleDecrementSubTitleAverageValue}
                >
                  <RemoveIcon />
                </Button>
              </Tooltip>
            )}
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography
                align={
                  widgetData?.data?.["align"] ? widgetData["align"] : "center"
                }
                className={classes.normal}
                style={{
                  fontSize: `${
                    widgetData?.data?.["averageSubTitleFontSize"] ?? 12
                  }px`,
                }}
              >
                {widgetData?.data?.subTitle}
              </Typography>
            </div>

            {widgetData?.data?.subTitle && (
              <Tooltip
                title={`Value Text Size: ${
                  widgetData?.data?.["averageSubTitleFontSize"] ?? 12
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    color: theme.palette.custom.orange.contrastText,
                    marginLeft: "8px",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                  onClick={handleIncrementSubTitleAverageValue}
                >
                  <AddIcon />
                </Button>
              </Tooltip>
            )}
          </div>
        </Grid>
      </Grid>
      <div
        className={classes.iconButtons}
        style={{
          display: "flex",
          position: "absolute",
          bottom: 8,
          left: 8,
          marginLeft: "8px",
          cursor: "pointer",
          gap: 4,
        }}
      >
        {widgetData?.data?.label === "Text Box" ||
        widgetData?.data?.label === "Text Only Box" ? (
          <ButtonCommon
            onClick={handleEdit}
            className="no-drag"
            style={{
              minWidth: "40px",
              maxWidth: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.custom.yellow.main,
            }}
          >
            <EditIcon style={{ color: "black" }} fontSize="small" />
          </ButtonCommon>
        ) : (
          (widgetData?.data?.label === "Title Only Box" ||
            widgetData?.data?.label === "Title Box" ||
            widgetData?.data?.label === "Heading Only Box" ||
            widgetData?.data?.label === "Heading") && (
            <ButtonCommon
              className="no-drag"
              style={{
                minWidth: "40px",
                maxWidth: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.palette.custom.yellow.main,
              }}
              onClick={handleEdit}
            >
              <EditIcon style={{ color: "black" }} fontSize="small" />
            </ButtonCommon>
          )
        )}
        <ButtonCommon
          className="no-drag"
          color="blue"
          style={{
            minWidth: "40px",
            maxWidth: "40px",
            marginLeft: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.custom.blue.main,
          }}
          onClick={() => handleCopy(widgetData.data)}
        >
          <FileCopyIcon style={{ color: "white" }} fontSize="small" />
        </ButtonCommon>
      </div>
    </Badge>
  );
};

export default TextWidgetBox;
